import { z } from 'zod';

export const staffMutationId = z.string().regex(/^[A-Za-z0-9_-]{8,100}$/);
export const staffPostSchema = z.object({
  clientMutationId: staffMutationId,
  body: z.string().trim().min(1).max(4000),
  parentId: z.string().length(26).nullable().default(null),
});
export const announcementSchema = z
  .object({
    clientMutationId: staffMutationId,
    title: z.string().trim().min(1).max(200),
    body: z.string().trim().min(1).max(4000),
    // A scheduling team picker expands a saved team into this explicit audience.
    recipientIds: z.array(z.string().min(1).max(255)).max(200).default([]),
    teamIds: z
      .array(z.string().regex(/^[0-9A-Z]{26}$/))
      .max(20)
      .default([]),
    audienceType: z.enum(['everyone', 'people', 'teams']).optional(),
    pinned: z.boolean().default(false),
    requiresAck: z.boolean().default(false),
  })
  .superRefine((data, ctx) => {
    const kind =
      data.audienceType ?? (data.teamIds.length ? 'teams' : data.recipientIds.length ? 'people' : 'everyone');
    if (
      (kind === 'everyone' && (data.recipientIds.length || data.teamIds.length)) ||
      (kind === 'people' && (!data.recipientIds.length || data.teamIds.length)) ||
      (kind === 'teams' && (!data.teamIds.length || data.recipientIds.length))
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['audienceType'],
        message: 'Choose everyone, at least one person, or at least one saved team.',
      });
    }
  });

export function announcementAudienceRequest(data: z.infer<typeof announcementSchema>): string {
  const recipients = [...new Set(data.recipientIds)].sort();
  const teams = [...new Set(data.teamIds)].sort();
  // Preserve replay compatibility for existing clients and stored announcements.
  if (!data.audienceType && !teams.length) return JSON.stringify(recipients);
  return JSON.stringify({ kind: data.audienceType ?? 'teams', recipientIds: recipients, teamIds: teams });
}

export const staffCopy = {
  en: {
    title: 'Staff',
    updates: 'Updates',
    newMessage: 'New message',
    shareUpdate: 'Share an update',
    send: 'Send',
    sendToEveryone: 'Send to: Everyone at this location',
    requestTimeOff: 'Request time off',
    swaps: 'Shift swaps',
    documents: 'Staff documents',
    myDocuments: 'My documents',
    preferences: 'Notification settings',
    peopleAndTeams: 'People and teams',
    addPhotos: 'Add a photo update',
    discardForPhotos:
      'Discard this update draft and start a separate photo update? Photos will be shared with everyone at this location.',
    close: 'Close draft',
    actions: 'Staff actions',
    resources: 'Staff resources',
    announcements: 'Announcements',
    feed: 'Team feed',
    post: 'Post',
    titleLabel: 'Title',
    body: 'Message',
    publish: 'Publish announcement',
    pinned: 'Pin announcement',
    requireAck: 'Require acknowledgement',
    read: 'Mark as read',
    ack: 'Acknowledge',
    readStatus: 'Read',
    ackStatus: 'Acknowledged',
    unread: 'Unread',
    pendingAck: 'Acknowledgement pending',
    report: 'Read and acknowledgement report',
    remove: 'Remove',
    removed: 'Removed by a manager.',
    former: 'Former team member',
    empty: 'No updates yet.',
    more: 'Load more',
    refresh: 'Refresh',
    retry: 'Retry the same change',
    offline: 'Connect to view current updates or submit changes. Your draft stays here while this page is open.',
    failed: 'The request could not be confirmed. Refresh or retry with the same draft.',
    plan: 'Staff is available with Pro and Business.',
    comment: 'Comment',
    reaction: 'Like',
    approved: 'Saved',
  },
  es: {
    title: 'Personal',
    updates: 'Novedades',
    newMessage: 'Nuevo mensaje',
    shareUpdate: 'Compartir una novedad',
    send: 'Enviar',
    sendToEveryone: 'Enviar a: Todos en esta ubicación',
    requestTimeOff: 'Solicitar tiempo libre',
    swaps: 'Cambios de turno',
    documents: 'Documentos del personal',
    myDocuments: 'Mis documentos',
    preferences: 'Ajustes de notificaciones',
    peopleAndTeams: 'Personas y equipos',
    addPhotos: 'Agregar una novedad con fotos',
    discardForPhotos:
      '¿Descartar este borrador y empezar una novedad con fotos por separado? Las fotos se compartirán con todos en esta ubicación.',
    close: 'Cerrar borrador',
    actions: 'Acciones del personal',
    resources: 'Recursos del personal',
    announcements: 'Anuncios',
    feed: 'Publicaciones del equipo',
    post: 'Publicar',
    titleLabel: 'Título',
    body: 'Mensaje',
    publish: 'Publicar anuncio',
    pinned: 'Fijar anuncio',
    requireAck: 'Solicitar confirmación',
    read: 'Marcar como leído',
    ack: 'Confirmar recepción',
    readStatus: 'Leído',
    ackStatus: 'Recepción confirmada',
    unread: 'Sin leer',
    pendingAck: 'Confirmación pendiente',
    report: 'Informe de lecturas y confirmaciones',
    remove: 'Eliminar',
    removed: 'Eliminado por un gerente.',
    former: 'Exintegrante del equipo',
    empty: 'Todavía no hay novedades.',
    more: 'Cargar más',
    refresh: 'Actualizar',
    retry: 'Reintentar el mismo cambio',
    offline:
      'Conéctese para ver las novedades o enviar cambios. Su borrador permanece aquí mientras esta página esté abierta.',
    failed: 'No se pudo confirmar la solicitud. Actualice o reintente con el mismo borrador.',
    plan: 'Personal está disponible con Pro y Business.',
    comment: 'Comentar',
    reaction: 'Me gusta',
    approved: 'Guardado',
  },
} as const;
